/**
 * طابور المزامنة غير المتصل (IndexedDB).
 * يُستخدم لإدخال السجل اليومي بدون إنترنت، ثم تُرسل الدفعات إلى /api/daily-entries/sync
 * مع مفتاح clientUuid لكل سطر لمنع التكرار.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { apiPost } from './api';

export interface PendingDailyEntry {
  clientUuid: string;
  date: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  ordersCount: number;
  cashCollected: string;
  cashDeposited: string;
  workedHours: string;
  notes: string | null;
  clientUpdatedAt: string;
  queuedAt: string;
  attempts: number;
  lastError?: string | null;
}

export interface SyncResultItem {
  clientUuid: string;
  status: 'created' | 'updated' | 'duplicate' | 'conflict' | 'rejected' | 'failed';
  messageAr?: string;
  stored?: Record<string, unknown>;
}

interface SarhaDB extends DBSchema {
  dailyQueue: {
    key: string;
    value: PendingDailyEntry;
    indexes: { byDate: string; byEmployee: string };
  };
  meta: {
    key: string;
    value: { key: string; value: string };
  };
}

let dbPromise: Promise<IDBPDatabase<SarhaDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<SarhaDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SarhaDB>('sarha-offline', 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('dailyQueue')) {
          const store = db.createObjectStore('dailyQueue', { keyPath: 'clientUuid' });
          store.createIndex('byDate', 'date');
          store.createIndex('byEmployee', 'employeeId');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

/** إضافة سطر إلى الطابور (يستبدل سطراً سابقاً لنفس السائق/التاريخ) */
export async function queueEntry(entry: PendingDailyEntry): Promise<void> {
  const db = await getDb();
  const existing = await db.getAllFromIndex('dailyQueue', 'byEmployee', entry.employeeId);
  const sameDay = existing.find((row) => row.date === entry.date);
  if (sameDay) {
    // آخر تعديل يفوز: نحدّث السطر القائم ونحتفظ بنفس المفتاح
    await db.put('dailyQueue', { ...entry, clientUuid: sameDay.clientUuid, queuedAt: sameDay.queuedAt });
    return;
  }
  await db.put('dailyQueue', entry);
}

/** كل الأسطر المنتظرة، مرتّبة بالأقدم أولاً */
export async function pendingEntries(): Promise<PendingDailyEntry[]> {
  const db = await getDb();
  const all = await db.getAll('dailyQueue');
  return all.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

export async function pendingCount(): Promise<number> {
  const db = await getDb();
  return db.count('dailyQueue');
}

export async function pendingForDate(date: string): Promise<PendingDailyEntry[]> {
  const db = await getDb();
  return db.getAllFromIndex('dailyQueue', 'byDate', date);
}

export async function removePending(clientUuid: string): Promise<void> {
  const db = await getDb();
  await db.delete('dailyQueue', clientUuid);
}

export async function clearQueue(): Promise<void> {
  const db = await getDb();
  await db.clear('dailyQueue');
}

async function markAttempt(entry: PendingDailyEntry, error?: string): Promise<void> {
  const db = await getDb();
  await db.put('dailyQueue', { ...entry, attempts: entry.attempts + 1, lastError: error ?? null });
}

export interface SyncOutcome {
  attempted: number;
  created: number;
  updated: number;
  duplicate: number;
  conflict: number;
  rejected: number;
  failed: number;
  details: SyncResultItem[];
}

/**
 * مزامنة الطابور مع الخادم.
 * تُرسل الأسطر على دفعات، وتُحذف من الطابور فقط ما نجحت مزامنته فعلاً.
 */
export async function syncQueue(batchSize = 50): Promise<SyncOutcome> {
  const outcome: SyncOutcome = {
    attempted: 0,
    created: 0,
    updated: 0,
    duplicate: 0,
    conflict: 0,
    rejected: 0,
    failed: 0,
    details: [],
  };

  const queue = await pendingEntries();
  if (queue.length === 0) return outcome;

  for (let index = 0; index < queue.length; index += batchSize) {
    const batch = queue.slice(index, index + batchSize);
    outcome.attempted += batch.length;

    try {
      const response = await apiPost<{ results: SyncResultItem[]; summary: Record<string, number> }>(
        '/api/daily-entries/sync',
        {
          entries: batch.map((entry) => ({
            clientUuid: entry.clientUuid,
            date: entry.date,
            employeeId: entry.employeeId,
            ordersCount: entry.ordersCount,
            cashCollected: entry.cashCollected,
            cashDeposited: entry.cashDeposited,
            workedHours: entry.workedHours,
            notes: entry.notes,
            clientUpdatedAt: entry.clientUpdatedAt,
          })),
        },
      );

      for (const result of response.results ?? []) {
        outcome.details.push(result);
        switch (result.status) {
          case 'created':
            outcome.created += 1;
            await removePending(result.clientUuid);
            break;
          case 'updated':
            outcome.updated += 1;
            await removePending(result.clientUuid);
            break;
          case 'duplicate':
            outcome.duplicate += 1;
            await removePending(result.clientUuid);
            break;
          case 'conflict':
            outcome.conflict += 1;
            // يبقى في الطابور ليُعاد بعد مراجعة المستخدم
            break;
          case 'rejected':
            outcome.rejected += 1;
            break;
          default:
            outcome.failed += 1;
            break;
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      for (const entry of batch) {
        await markAttempt(entry, message);
        outcome.failed += 1;
        outcome.details.push({ clientUuid: entry.clientUuid, status: 'failed', messageAr: message });
      }
      // فشل الشبكة: نتوقف عن بقية الدفعات
      break;
    }
  }

  const db = await getDb();
  await db.put('meta', { key: 'lastSyncAt', value: new Date().toISOString() });
  return outcome;
}

export async function lastSyncAt(): Promise<string | null> {
  const db = await getDb();
  const row = await db.get('meta', 'lastSyncAt');
  return row?.value ?? null;
}

/** سجل آخر مزامنة مفصل (للعرض في شاشة السجل اليومي) */
export async function saveLastOutcome(outcome: SyncOutcome): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: 'lastOutcome', value: JSON.stringify(outcome) });
}

export async function readLastOutcome(): Promise<SyncOutcome | null> {
  const db = await getDb();
  const row = await db.get('meta', 'lastOutcome');
  if (!row?.value) return null;
  try {
    return JSON.parse(row.value) as SyncOutcome;
  } catch {
    return null;
  }
}
